import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SERVER_API_URL } from '../../app.constants';
import { ParamContext } from '../../shared/service/param.context';
import { EnvironmentFieldmapInfoModel } from '../../study-manager/field-map/environment-fieldmap-info.model';
import { FieldMapModel } from '../../study-manager/field-map/field-map.model';
import { FieldMapSettingsModel } from '../../study-manager/field-map/field-map-settings.model';

export interface FieldMapInfoSearchRequest {
    programUUID?: string;
    studyId?: number;
    instanceId?: number;
    withGeoJsonOnly?: boolean
}

export interface FieldmapInfo {
    crop?: string;
    programName?: string;
    studyName?: string;
    instanceId?: number;
    instanceNumber?: number;
    locationName?: number;
    entriesCount?: number;
    replicatesCount?: number;
    plotsCount?: number;
    columnCount?: number;
    rangeCount?: number;
}

@Injectable({
    providedIn: 'root'
})
export class FieldmapService {

    constructor(private http: HttpClient,
                private paramContext: ParamContext) {
    }

    /**
     * Search for fieldmap information with pagination
     * @param cropName The name of the crop
     * @param searchRequest The search criteria
     * @param page Page number (0-based)
     * @param size Number of items per page
     * @returns Observable of fieldmap information array and sets total count in pagination service
     */
    searchFieldMapInfo(
        cropName: string,
        searchRequest: FieldMapInfoSearchRequest,
        page = 0,
        size = 20
    ): Observable<{ results: FieldmapInfo[], total: number }> {
        const url = `${SERVER_API_URL}crops/${cropName}/fieldmap/search`;
        const params = new HttpParams()
            .set('page', page.toString())
            .set('size', size.toString());

        return this.http.post<FieldmapInfo[]>(url, searchRequest, {
            params,
            observe: 'response'
        }).pipe(
            map((response: HttpResponse<FieldmapInfo[]>) => ({
                results: response.body || [],
                total: Number(response.headers.get('X-Total-Count')) || 0
            }))
        );
    }

    getEnvironmentFieldmapInfo(studyId: number, instanceIds: number[]): Observable<EnvironmentFieldmapInfoModel[]> {
        let url = `${SERVER_API_URL}crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/fieldmap-info`;
        if (instanceIds && instanceIds.length) {
            url += `?instanceIds=${instanceIds}`;
        }
        return this.http.get<EnvironmentFieldmapInfoModel[]>(url);
    }

    deleteGeoreferences(cropName: string, instanceId: string): Observable<any> {
        return this.http.delete<void>(`${SERVER_API_URL}crops/${cropName}/fieldmap/georeferences/${instanceId}`);
    }

    generateFieldmapPlots(studyId: number, settings: FieldMapSettingsModel): Observable<FieldMapModel> {
        return this.http.post<FieldMapModel>(
            `${SERVER_API_URL}crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/generate-fieldmap`, settings, {
                observe: 'response'
            }).pipe(map((res: HttpResponse<FieldMapModel>) => res.body));
    }

    saveFieldMap(studyId: number, settings: FieldMapSettingsModel): Observable<void> {
        return this.http.post<void>(`${SERVER_API_URL}crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/save-fieldmap`, settings);
    }

    viewFieldMap(studyId: number, instanceId: number): Observable<FieldMapModel> {
        return this.http.get<FieldMapModel>(
            `${SERVER_API_URL}crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/view-fieldmap/${instanceId}`);
    }

    deleteFieldMap(studyId: number, instanceIds: number[]): Observable<string[]> {
        return this.http.delete<string[]>(
            `${SERVER_API_URL}crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/delete-fieldmap?instanceIds=${instanceIds}`);
    }

    exportFieldmap(studyId: number, isSaved: boolean, settings: FieldMapSettingsModel) {
        return this.http.post(`${SERVER_API_URL}crops/${this.paramContext.cropName}/programs/${this.paramContext.programUUID}/studies/${studyId}/export-fieldmap/${isSaved}`, settings,
            {
                responseType: 'blob',
                observe: 'response'
            }
        );
    };
}
